const moment = require('moment');
const orderBy = require('lodash/orderBy');

function slugify(value) {
  return value
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');
}

function isValidSlug(value) {
  if (!value) {
    return false;
  }
  return /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(value);
}

function urlForNewCar(carVariant, section) {
  const slug =
    carVariant?.slug?.current ?? slugify(getNewCarFullName(carVariant));

  if (!slug) {
    return '';
  }

  return `/new-cars/${slug}${section ? '#' + section : ''}`;
}

function urlForCarModel(carModel) {
  const slug =
    carModel?.slug?.current ||
    carModel.name
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');

  return `/new-cars/car-model/${slug}`;
}

function urlForUsedCar(usedCar, section) {
  const slug = usedCar.name
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');

  let s = section;
  if (!s) {
    s = 'detail';
  }

  return `/used-cars/${slug}/${s}/${usedCar._id}`;
}

function slugForMakeProfile(make) {
  return slug =
    make?.slug?.current ||
    make.name
      .toLowerCase()
      .replace(/[^\w- ]+/g, '')
      .replace(/ +/g, '-');
}

function urlForMakeProfile(make) {
  const slug = slugForMakeProfile(make);
  return `/new-cars/${slug}`;
}

function calculateDepreciation(carPrice, coeRebate, parfRebate, coeExpiryDate) {
  const deregistrationValue = coeRebate + parfRebate;
  const remainingCOEYears = moment().diff(coeExpiryDate, 'years', true);
  return Math.floor(
    (carPrice - deregistrationValue) / Math.abs(remainingCOEYears)
  );
}

function calculateUsedCarDepreciation(usedCarInfo) {
  return calculateDepreciation(
    Number(usedCarInfo.list_price),
    Number(usedCarInfo.coe_rebate),
    Number(usedCarInfo.parf_rebate_amount),
    usedCarInfo.coe_expiry_date
  );
}

function calculateArfFromOmv(omv) {
  let percent;
  if (omv <= 20000) {
    percent = 100;
  } else if (omv > 20000 && omv <= 30000) {
    percent = 140;
  } else {
    percent = 180;
  }
  return (omv * percent) / 100;
}

function calculateNewCarDepreciation(variant) {
  if (variant.price === 0) return 0;
  const arf = calculateArfFromOmv(variant.carOverview.omv);
  const parfRebate = arf / 2;
  const deregistrationValue = parfRebate;
  return (variant.price - deregistrationValue) / 10;
}

const DEFAULT_INTEREST = 2.78;
const DEFAULT_PERIOD_YEARS = 7;

const calculateLoan = (
  carPrice,
  downPayment,
  interestRate = DEFAULT_INTEREST,
  periodYears = DEFAULT_PERIOD_YEARS
) => {
  if (!downPayment) {
    downPayment = (carPrice * 30) / 100;
  }
  const loanAmount = carPrice - downPayment;
  const totalInterest = (interestRate / 100) * periodYears * loanAmount;
  const monthlyInstallment = Math.floor(
    (loanAmount + totalInterest) / (periodYears * 12)
  );
  const totalPaidOnLoan = totalInterest + loanAmount;

  return {
    totalInterest,
    monthlyInstallment,
    loanAmount,
    loanTerm: periodYears,
    interestRate: interestRate,
    totalPaidOnLoan,
  };
};

const calculateUsedCarLoan = (usedCarInfo) => {
  const price = Number(usedCarInfo.list_price);
  const downPayment = (price * 30) / 100;
  return calculateLoan(price, downPayment);
};

// https://www.directasia.com/blog/road-tax-singapore/
const calculateBaseRoadTaxForPetrolCar = (engineCapacity) => {
  if (engineCapacity < 600) {
    return 400 * 0.782;
  } else if (engineCapacity > 600 && engineCapacity <= 1000) {
    return (400 + 0.25 * (engineCapacity - 600)) * 0.782;
  } else if (engineCapacity > 1000 && engineCapacity <= 1600) {
    return (500 + 0.75 * (engineCapacity - 1000)) * 0.782;
  } else if (engineCapacity > 1600 && engineCapacity <= 3000) {
    return (950 + 1.5 * (engineCapacity - 1600)) * 0.782;
  } else {
    return (3050 + 2 * (engineCapacity - 3000)) * 0.782;
    // > 3000
  }
};

const calculateBaseRoadTaxForElectricCar = (powerRate) => {
  if (powerRate < 7.5) {
    return 200 * 0.782;
  } else if (powerRate > 7.5 && powerRate <= 30) {
    return (200 + 2 * (powerRate - 7.5)) * 0.782;
  } else if (powerRate > 30 && powerRate <= 90) {
    return (250 + 3.75 * (powerRate - 30)) * 0.782;
  } else if (powerRate > 90 && powerRate <= 230) {
    return (475 + 7.5 * (powerRate - 90)) * 0.782;
  } else {
    return (1525 + 10 * (powerRate - 230)) * 0.782;
    // > 230
  }
};

const calculateBaseRoadTax = (engineCapacity, fuelType) => {
  if (fuelType === 'Electric') {
    return calculateBaseRoadTaxForElectricCar(engineCapacity);
  } else {
    return calculateBaseRoadTaxForPetrolCar(engineCapacity);
  }
};

const calculateUsedCarRoadTax = (usedCar) => {
  let baseRoadTax = calculateBaseRoadTax(
    Number(usedCar.engine_cap),
    usedCar.fuel_type
  );

  const age = moment().diff(usedCar.reg_date, 'years', true);

  let additionalRoadTaxPercent = 0;
  if (age > 10 && age <= 11) {
    additionalRoadTaxPercent = 10;
  } else if (age > 11 && age <= 12) {
    additionalRoadTaxPercent = 20;
  } else if (age > 12 && age <= 13) {
    additionalRoadTaxPercent = 30;
  } else if (age > 13 && age <= 14) {
    additionalRoadTaxPercent = 40;
  } else if (age > 14) {
    additionalRoadTaxPercent = 50;
  }

  return baseRoadTax + (baseRoadTax * additionalRoadTaxPercent) / 100;
};

const getCarCOECategory = (engineCapacity, maxPowerBhp, fuelType) => {
  if (!engineCapacity || !maxPowerBhp) return null;

  if (fuelType === 'Electric') {
    if (maxPowerBhp <= 147) {
      return 'A';
    } else {
      return 'B';
    }
  }

  if (engineCapacity <= 1600 && maxPowerBhp <= 130) {
    return 'A';
  } else {
    return 'B';
  }
};

const getNewCarFullName = (variant, noYear = false) => {
  let fullNameParts = [];
  if (variant.model?.make?.name) {
    fullNameParts.push(variant.model.make?.name);
  }
  if (variant.model?.name) {
    fullNameParts.push(variant.model.name);
  }
  if (variant.name) {
    fullNameParts.push(variant.name);
  }
  if (variant.year && !noYear) {
    fullNameParts.push(variant.year);
  }
  return fullNameParts.join(' ');
};

const sortAllMakesByLatestPriceList = (allMakes) => {
  const makesArr = [];

  allMakes.map((make) => {
    const latestDates = []
    make.dealers.forEach((makeDealer) => {
      const validPriceLists = makeDealer.priceLists.filter(pl => pl.datePublished && moment(pl.datePublished).isValid());
      const sortedPriceLists = orderBy(validPriceLists, (pl) => moment(pl.datePublished).valueOf(), 'desc');
      if (sortedPriceLists[0] && sortedPriceLists[0].file.asset) {
        makeDealer.latestPriceList = sortedPriceLists[0];
        latestDates.push(moment(sortedPriceLists[0].datePublished));
      }
    });
    
    if (latestDates.length > 0) {
      make.latestDatePublished = moment.max(latestDates);
    }

    makesArr.push(make);
  });

  return orderBy(makesArr, (m) => m.latestDatePublished || '', 'desc');
};

const sortAllMakesByAlphabetical = (allMakes) => {
  return orderBy(allMakes, (m) => m.name.toLowerCase());
};

module.exports.urlForNewCar = urlForNewCar;
module.exports.urlForCarModel = urlForCarModel;
module.exports.urlForUsedCar = urlForUsedCar;
module.exports.slugForMakeProfile = slugForMakeProfile;
module.exports.urlForMakeProfile = urlForMakeProfile;
module.exports.calculateDepreciation = calculateDepreciation;
module.exports.calculateNewCarDepreciation = calculateNewCarDepreciation;
module.exports.calculateUsedCarDepreciation = calculateUsedCarDepreciation;
module.exports.calculateLoan = calculateLoan;
module.exports.calculateUsedCarLoan = calculateUsedCarLoan;
module.exports.calculateUsedCarRoadTax = calculateUsedCarRoadTax;
module.exports.getCarCOECategory = getCarCOECategory;
module.exports.getNewCarFullName = getNewCarFullName;
module.exports.slugify = slugify;
module.exports.sortAllMakesByLatestPriceList = sortAllMakesByLatestPriceList;
module.exports.sortAllMakesByAlphabetical = sortAllMakesByAlphabetical;

module.exports.DEFAULT_INTEREST = DEFAULT_INTEREST;
module.exports.DEFAULT_PERIOD_YEARS = DEFAULT_PERIOD_YEARS;
